import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Paper,
    IconButton,
    Typography,
    Box,
    Divider,
    Button,
    TextField,
    InputAdornment,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MergeIcon from '@mui/icons-material/Merge';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTranslation } from 'react-i18next';
import MergeSortCodes from './MergeSortCodes';

// **Enhanced Styling**
const dialogBoxStyle = {
    bgcolor: 'background.paper',
    borderRadius: '12px',
    boxShadow: 24,
    zIndex: '1000 !important', // Ensure lower than AccountantDetailsModals
    overflow: 'hidden',
    maxWidth: '90%',
    width: '90%',
};
const stickyHeaderStyle = {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: '#f5f5f5',
};

const titleStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    p: 2,
    pb: 1,
    fontWeight: 'bold',
};

const tableHeadCellStyle = {
    fontWeight: '600',
    backgroundColor: '#f5f5f5',
};

const cellStyle = {
    fontSize: '0.9rem',
};

export default function DataTableDialog({
    open,
    onClose,
    data = [],
    title = 'Details',
    rowsPerPageOptions = [50, 70, 100], // Bigger page sizes
    defaultRowsPerPage = 50,
    searchPlaceholder = 'Search',
    showMergeButton = false, // New prop to control merge button visibility
    onMerge = null, // Callback for single merge functionality (legacy)
    onBatchMerge = null, // Callback for batch merge functionality
}) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
    const [isMergeModalVisible, setMergeModalVisible] = useState(false);
    const { i18n, t } = useTranslation();
    const direction = i18n.dir();
    
    // **Extract `onEdit` & `onDelete` handlers and remove them from the row data**
    const cleanedData = useMemo(() => {
        return data.map(({ onEdit, onDelete, ...rest }) => ({
            ...rest,
            actions: { onEdit, onDelete }, // Store callbacks in a separate key
        }));
    }, [data]);

    // **Derive Columns & Add "Actions" Column**
    const derivedColumns = useMemo(() => {
        if (!cleanedData.length) return [];
        const keys = Object.keys(cleanedData[0]).filter(key => key !== 'value' && key !== 'actions' && key !== 'sortCodeId' && key !== 'sortCodeName' && key !== 'isEditable'); // Exclude "value", "actions", "sortCodeId", "sortCodeName" & "isEditable"
        return [...keys.map((key) => ({
            id: key,
            label: t(key), // Apply translation function
        })), { id: 'actions', label: t('Actions') }];
    }, [cleanedData, t]);

    // **Handle Search and Sorting**
    const filteredAndSortedData = useMemo(() => {
        // First, apply search filter
        let result = cleanedData;
        
        if (searchTerm.trim()) {
            const lowerSearch = searchTerm.trim().toLowerCase();
            result = cleanedData.filter((row) => {
                return Object.entries(row).some(([key, value]) => {
                    if (key === 'actions') return false; // Ignore actions column

                    const keyMatch = key.toLowerCase().includes(lowerSearch); // Search in column name
                    const valueMatch =
                        typeof value === 'string'
                            ? value.toLowerCase().includes(lowerSearch)
                            : typeof value === 'number' || typeof value === 'boolean'
                                ? String(value).toLowerCase().includes(lowerSearch)
                                : false; // Ignore non-primitive values

                    return keyMatch || valueMatch; // Match in either key or value
                });
            });
        }

        // Then, apply sorting across the FULL dataset (not just the current page)
        if (sortBy && sortBy !== 'actions') {
            const directionFactor = sortDirection === 'asc' ? 1 : -1;

            // Helper: treat numeric strings ("10", "  -3.5 ") as numbers
            const toNumber = (val) => {
                if (typeof val === 'number') return Number.isNaN(val) ? null : val;
                if (typeof val === 'string') {
                    const trimmed = val.trim();
                    if (trimmed === '') return null;
                    // Only convert when the whole string is a valid number
                    const num = Number(trimmed);
                    return Number.isNaN(num) ? null : num;
                }
                return null;
            };

            result = [...result].sort((a, b) => {
                const aValue = a[sortBy];
                const bValue = b[sortBy];

                // Handle null/undefined/empty values (always push to the end)
                const aEmpty = aValue == null || (typeof aValue === 'string' && aValue.trim() === '');
                const bEmpty = bValue == null || (typeof bValue === 'string' && bValue.trim() === '');
                if (aEmpty && bEmpty) return 0;
                if (aEmpty) return 1;
                if (bEmpty) return -1;

                // Numeric comparison when both values are numbers (or numeric strings)
                const aNum = toNumber(aValue);
                const bNum = toNumber(bValue);
                if (aNum != null && bNum != null) {
                    return (aNum - bNum) * directionFactor;
                }

                // Natural, locale-aware string comparison (handles Hebrew, mixed text/numbers)
                return (
                    String(aValue).localeCompare(String(bValue), undefined, {
                        numeric: true,
                        sensitivity: 'base',
                    }) * directionFactor
                );
            });
        }

        return result;
    }, [cleanedData, searchTerm, sortBy, sortDirection]);




    // **Paginated Data**
    const paginatedData = useMemo(() => {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return filteredAndSortedData.slice(startIndex, endIndex);
    }, [filteredAndSortedData, page, rowsPerPage]);

    // **Handle Sort**
    const handleSort = (columnId) => {
        if (columnId === 'actions') return; // Don't sort actions column

        if (sortBy === columnId) {
            // Toggle sort direction if clicking the same column
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new column and default to ascending
            setSortBy(columnId);
            setSortDirection('asc');
        }
        setPage(0); // Reset to first page when sorting changes
    };

    // **Pagination Handling**
    const handleClose = () => {
        setPage(0);
        setRowsPerPage(defaultRowsPerPage);
        setSearchTerm('');
        setSortBy(null);
        setSortDirection('asc');
        onClose();
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
        <Dialog open={open} onClose={handleClose} PaperProps={{ sx: dialogBoxStyle }} sx={{ zIndex: 1000 }} disableEnforceFocus disableRestoreFocus>
            {/* Header */}
            <Box sx={titleStyle} className={`${direction}`}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                    {t(title)}
                </Typography>
                <IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider />

            {/* Search Bar and Merge Button */}
            <Box sx={{ px: 2, py: 1, display: 'flex', gap: 2, alignItems: 'center' }} className={`${direction}`}>
                <TextField
                    placeholder={t(searchPlaceholder)}
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                {showMergeButton && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<MergeIcon />}
                        onClick={() => setMergeModalVisible(true)}
                        sx={{
                            minWidth: '120px',
                            height: '40px',
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        {t('MergeSortCodes')}
                    </Button>
                )}
            </Box>

            {/* Table Container with Scrollable Content */}
            <DialogContent dividers sx={{ p: 0, maxHeight: '70vh', overflowY: 'auto' }} className={`${direction}`} >
                <TableContainer
                    dir={direction}
                    component={Paper}
                    sx={{ maxHeight: '55vh', overflowY: 'auto', boxShadow: 'none' }}
                >
                    <Table size="small" stickyHeader className="sticky-header">
                        <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                {derivedColumns.map((col) => (
                                    <TableCell 
                                        key={col.id} 
                                        sx={{ 
                                            ...cellStyle, 
                                            ...tableHeadCellStyle,
                                            cursor: col.id !== 'actions' ? 'pointer' : 'default',
                                            userSelect: 'none',
                                            '&:hover': col.id !== 'actions' ? { backgroundColor: '#e0e0e0' } : {}
                                        }}
                                        onClick={() => handleSort(col.id)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography component="span" sx={{ fontWeight: 600 }}>
                                                {col.label}
                                            </Typography>
                                            {col.id !== 'actions' && sortBy === col.id && (
                                                sortDirection === 'asc' ? (
                                                    <ArrowUpwardIcon sx={{ fontSize: '16px', color: 'primary.main' }} />
                                                ) : (
                                                    <ArrowDownwardIcon sx={{ fontSize: '16px', color: 'primary.main' }} />
                                                )
                                            )}
                                        </Box>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedData.length ? (
                                paginatedData.map((row, idx) => (
                                    <TableRow key={idx}>
                                        {derivedColumns.map((col) => (
                                            <TableCell key={col.id} sx={cellStyle}>
                                                {col.id === 'actions' ? (
                                                    <Box display="flex">
                                                        {row.actions.onEdit && (
                                                            <Tooltip title={t('Edit')}>
                                                                <IconButton
                                                                    onClick={(event) => {
                                                                        event.stopPropagation(); // Prevent dialog from closing
                                                                        row.actions.onEdit(row);
                                                                    }}
                                                                    size="small"
                                                                >
                                                                    <EditIcon color="primary" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {row.actions.onDelete && (
                                                            <Tooltip title={t('Delete')}>
                                                                <IconButton
                                                                    onClick={(event) => {
                                                                        event.stopPropagation(); // Prevent dialog from closing
                                                                        row.actions.onDelete(row);
                                                                    }}
                                                                    size="small"
                                                                >
                                                                    <DeleteIcon color="error" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    row[col.id] != null ? String(row[col.id]) : ''
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={derivedColumns.length} align="center">
                                        <Typography variant="body2" sx={{ color: 'gray' }}>
                                            {t('NoDataAvailable')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            {/* Pagination & Close Button */}
            <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', mt: 1 }}>
                <TablePagination
                    component="div"
                    count={filteredAndSortedData.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                    labelRowsPerPage={t('Rows per page')}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${t('of')} ${count !== -1 ? count : `${t('more than')} ${to}`}`
                    }
                />

            </DialogActions>
        </Dialog>

        {/* Merge Sort Codes Modal */}
        {showMergeButton && (
            <MergeSortCodes
                isVisible={isMergeModalVisible}
                onClose={() => setMergeModalVisible(false)}
                sortCodesData={cleanedData}
                handleSortCodeMerge={onMerge}
                handleBatchMerge={onBatchMerge}
            />
        )}
    </>
    );
}
